import React, { useEffect, useRef } from "react";
import "./SpaceBackground.scss";

const SpaceBackground = () => {
  console.log("SpaceBackground component rendered");
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const shootingStarsRef = useRef([]);
  const nebulaRef = useRef(null);

  // Initialize stars
  useEffect(() => {
    console.log("Canvas ref:", canvasRef.current);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Set canvas to full window size
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Re-create stars on resize
      createStars();
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    // Create initial stars
    createStars();
    
    // Animation loop
    let animationFrameId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawStars(ctx);
      updateAndDrawShootingStars(ctx);
      animationFrameId = window.requestAnimationFrame(animate);
    };
    
    animate();
    
    // Create a new shooting star every 3-8 seconds
    const shootingStarInterval = setInterval(() => {
      if (Math.random() > 0.5) { // 50% chance to create a shooting star
        createShootingStar();
      }
    }, 3000 + Math.random() * 5000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.cancelAnimationFrame(animationFrameId);
      clearInterval(shootingStarInterval);
    };
  }, []);
  
  // Create stars with random properties
  const createStars = () => {
    const canvas = canvasRef.current;
    const stars = [];
    
    // Create 200 stars with random properties
    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5 + 0.5,
        opacity: Math.random(),
        twinkleSpeed: 0.003 + Math.random() * 0.03,
        twinkleDirection: Math.random() > 0.5 ? 1 : -1,
        twinklePhase: Math.random() * Math.PI * 2
      });
    }
    
    starsRef.current = stars;
  };
  
  // Create a shooting star
  const createShootingStar = () => {
    const canvas = canvasRef.current;
    
    // Random starting position on the top half of the screen
    const startX = Math.random() * canvas.width;
    const startY = Math.random() * (canvas.height / 2);
    
    // Random angle (mostly downward)
    const angle = (Math.PI / 4) + (Math.random() * Math.PI / 2);
    
    // Speed and length
    const speed = 5 + Math.random() * 10;
    const length = 50 + Math.random() * 80;
    
    shootingStarsRef.current.push({
      x: startX,
      y: startY,
      angle: angle,
      speed: speed,
      length: length,
      opacity: 1,
      // Trail is the number of positions to remember
      trail: [],
      // Maximum number of positions to remember
      maxTrail: 10,
      // Fading speed
      fade: 0.02 + Math.random() * 0.02
    });
  };
  
  // Update and draw shooting stars
  const updateAndDrawShootingStars = (ctx) => {
    const canvas = canvasRef.current;
    const shootingStars = shootingStarsRef.current;
    
    // Update and draw each shooting star
    for (let i = shootingStars.length - 1; i >= 0; i--) {
      const star = shootingStars[i];
      
      // Move the star
      star.x += Math.cos(star.angle) * star.speed;
      star.y += Math.sin(star.angle) * star.speed;
      
      // Add current position to trail
      star.trail.push({ x: star.x, y: star.y, opacity: star.opacity });
      
      // Limit trail length
      if (star.trail.length > star.maxTrail) {
        star.trail.shift();
      }
      
      // Fade out
      star.opacity -= star.fade;
      
      // Draw the shooting star
      if (star.trail.length > 1 && star.opacity > 0) {
        ctx.beginPath();
        
        // Create gradient for the trail
        const gradient = ctx.createLinearGradient(
          star.trail[0].x, star.trail[0].y,
          star.x, star.y
        );
        
        gradient.addColorStop(0, `rgba(255, 255, 255, 0)`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${star.opacity})`);
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.moveTo(star.trail[0].x, star.trail[0].y);
        
        // Draw the trail
        for (let j = 1; j < star.trail.length; j++) {
          ctx.lineTo(star.trail[j].x, star.trail[j].y);
        }
        
        ctx.stroke();
        
        // Draw the star head
        ctx.beginPath();
        ctx.arc(star.x, star.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
      }
      
      // Remove shooting stars that are off screen or fully faded
      if (star.x < 0 || star.x > canvas.width || 
          star.y < 0 || star.y > canvas.height || 
          star.opacity <= 0) {
        shootingStars.splice(i, 1);
      }
    }
  };
  
  // Draw stars with twinkling effect
  const drawStars = (ctx) => {
    starsRef.current.forEach(star => {
      // Update star twinkling
      star.twinklePhase += star.twinkleSpeed;
      const twinkleFactor = 0.5 + 0.5 * Math.sin(star.twinklePhase);
      const currentOpacity = 0.2 + star.opacity * twinkleFactor;
      
      // Draw star
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${currentOpacity})`;
      ctx.fill();
    });
  };

  return (
    <div className="space-background">
      <canvas ref={canvasRef} className="stars-canvas"></canvas>
      <div className="nebula-overlay"></div>
    </div>
  );
};

export default SpaceBackground;